import React from 'react';
import { useTheme } from 'styled-components';
import { cn } from '../../../../../utils/tailwind';
import { createStableClassToken } from '../../../../../utils/stable-class-token';
import { GapSizeType } from '../../../../../theme/type/gap-type';
import { ButtonSizeType, ButtonVariantType, ButtonVariationType } from '../../../../../theme/type/button-type';
import { BreakpointSizeType } from '../../../../../theme/type/breakpoints-type';

interface ButtonIconPropsInterface {
    icon?: React.ReactNode;
    size: ButtonSizeType | object;
    hideLabelUnder?: BreakpointSizeType;
    hideLabelOver?: BreakpointSizeType;
    variation?: ButtonVariationType;
    variant?: ButtonVariantType;
    gap?: GapSizeType;
    position?: 'left' | 'right';
    onClick?: (event: React.MouseEvent<HTMLAnchorElement & HTMLButtonElement & HTMLDivElement>) => void;
    onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export const ButtonIcon: React.FC<ButtonIconPropsInterface> = ({
    icon,
    variant = 'default',
    size,
    variation = 'filled',
    gap = 'medium',
    hideLabelUnder,
    hideLabelOver,
    position,
    onClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const theme = useTheme();
    const classToken = createStableClassToken('button-icon', [
        variant,
        variation,
        size,
        hideLabelUnder,
        hideLabelOver,
        gap,
        position,
    ]);

    const { base } = theme.button.variant[variant][variation];

    const baseStyle: React.CSSProperties = {
        cursor: onClick ? 'pointer' : undefined,
    };

    if (typeof size === 'string') {
        baseStyle.width = theme.button.size[size].icon.wrapper;
        baseStyle.height = theme.button.size[size].icon.wrapper;
    }

    if (position === 'right') {
        if (!hideLabelUnder && !hideLabelOver) {
            baseStyle.marginLeft = theme.gap[gap];
        }
    }

    if (position === 'left') {
        baseStyle.order = -1;
        if (!hideLabelUnder && !hideLabelOver) {
            baseStyle.marginRight = theme.gap[gap];
        }
    }

    const responsiveCss =
        typeof size === 'object'
            ? (Object.entries(size) as [BreakpointSizeType, ButtonSizeType][])
                  .map(([breakpoint, buttonSize], index) => {
                      const width = theme.button.size[buttonSize].icon.wrapper;
                      const iconSize = theme.button.size[buttonSize].icon.size;
                      const maxQuery = theme.breakpoints[breakpoint].max;
                      const minQuery = theme.breakpoints[breakpoint].min;

                      const chunk = (query: string) => `
                        @media ${query} {
                            .${classToken} {
                                width: ${width};
                                height: ${width};
                            }

                            .${classToken} svg {
                                width: ${iconSize} !important;
                                height: ${iconSize} !important;
                            }
                        }
                    `;

                      if (index === 0) {
                          return `${chunk(maxQuery)} ${chunk(minQuery)}`;
                      }

                      return chunk(minQuery);
                  })
                  .join('\n')
            : '';

    const toggleCss = typeof size === 'string' ? theme.button.size[size].icon.size : undefined;

    const styleElement = (
        <style>{`
                .${classToken} {
                    color: ${base.icon.color};
                    background: ${!hideLabelUnder && !hideLabelOver ? base.icon.background : 'transparent'};
                }

                .${classToken} > * {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }

                .${classToken} svg {
                    display: inline-flex;
                    align-items: center;
                    width: inherit;
                    height: inherit;
                    stroke-width: 0;
                    fill: transparent;
                    margin: 0;
                    transition: color ${theme.base.transition};
                }

                .${classToken} i,
                .${classToken} svg {
                    color: inherit !important;
                }

                ${
                    typeof size === 'string'
                        ? `
                    .${classToken} svg {
                        width: ${theme.button.size[size].icon.size} !important;
                        height: ${theme.button.size[size].icon.size} !important;
                    }
                `
                        : ''
                }

                ${
                    hideLabelUnder && toggleCss
                        ? `
                    @media ${theme.breakpoints[hideLabelUnder].min} {
                        .${classToken} {
                            background: ${base.icon.background};
                            ${position === 'right' ? `margin-left: ${theme.gap[gap]};` : ''}
                            ${position === 'left' ? `margin-right: ${theme.gap[gap]};` : ''}
                        }

                        .${classToken} svg {
                            width: ${toggleCss} !important;
                            height: ${toggleCss} !important;
                        }
                    }

                    @media ${theme.breakpoints[hideLabelUnder].max} {
                        .${classToken} svg {
                            width: calc(${toggleCss} + 0.4rem) !important;
                            height: calc(${toggleCss} + 0.4rem) !important;
                        }
                    }
                `
                        : ''
                }

                ${
                    hideLabelOver && toggleCss
                        ? `
                    @media ${theme.breakpoints[hideLabelOver].max} {
                        .${classToken} {
                            background: ${base.icon.background};
                            ${position === 'right' ? `margin-left: ${theme.gap[gap]};` : ''}
                            ${position === 'left' ? `margin-right: ${theme.gap[gap]};` : ''}
                        }

                        .${classToken} svg {
                            width: ${toggleCss} !important;
                            height: ${toggleCss} !important;
                        }
                    }

                    @media ${theme.breakpoints[hideLabelOver].min} {
                        .${classToken} svg {
                            width: calc(${toggleCss} + 0.4rem) !important;
                            height: calc(${toggleCss} + 0.4rem) !important;
                        }
                    }
                `
                        : ''
                }

                ${responsiveCss}
            `}</style>
    );

    return (
        <>
            {styleElement}
            <span
                data-button-icon
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={cn(classToken, 'relative inline-flex items-center justify-center rounded-full')}
                style={baseStyle}
            >
                {icon}
            </span>
        </>
    );
};
